import { Link } from "react-router-dom";
import type { SituationDetail } from "@mosaic/shared";
import type { FactResponse } from "../api/client.js";
import { filingHref } from "../lib/citations.js";

/**
 * What each side of the separation does, in the filing's own words.
 *
 * **Every word here is quoted, not written.** These are `parent_business` and
 * `spinco_business` facts: verbatim passages located character-for-character in the
 * document, like any other fact. A summary Mosaic composed would be a claim the quote
 * check cannot be run on.
 *
 * The quotation marks separate "the company says this about itself" from "Mosaic says
 * this about the company".
 */

interface Side {
  label: string;
  name: string;
  cik: string | null;
  ticker: string | null;
  fact: FactResponse | undefined;
}

/**
 * One side of the separation: its name, the quoted business description with a link to its
 * source, and for SpinCo the competitors the filing names.
 *
 * @internal
 * @param side The company and its business fact, if one has been read.
 * @param peers The `spinco_peers` fact, for the side it belongs to.
 */
function Block({ side, peers }: { side: Side; peers?: FactResponse }) {
  const names = Array.isArray(peers?.value) ? (peers.value as string[]) : [];

  return (
    <section className="biz">
      <div className="biz-hd">
        <span className="biz-role">{side.label}</span>
        {/* Linked only where there is a company page to open: a parent read out of prose
            with no CIK has none. */}
        {side.cik ? (
          <Link className="biz-name" to={`/companies/${side.cik}`}>
            {side.name}
          </Link>
        ) : (
          <span className="biz-name">{side.name}</span>
        )}
        {side.ticker && <span className="biz-ticker">{side.ticker}</span>}
      </div>

      {side.fact ? (
        <>
          <blockquote className="biz-quote">{String(side.fact.value)}</blockquote>
          <p className="biz-cite">
            {side.fact.document ? (
              <Link
                to={filingHref(side.fact.document.accession, {
                  documentId: side.fact.document.id,
                  charStart: side.fact.charStart,
                  charEnd: side.fact.charEnd,
                })}
              >
                {side.fact.document.formType ?? "filing"}
              </Link>
            ) : (
              "quoted from the filings"
            )}
            {/* `rule` or `llm`, in the same words the terms grid prints. */}
            <span className="biz-method"> · {side.fact.method}</span>
          </p>
        </>
      ) : (
        <p className="biz-none">
          No filing read so far describes this company's business. That is an absence of
          reading, not an absence of a business.
        </p>
      )}

      {names.length > 0 && (
        <div className="biz-peers">
          <span className="biz-peers-k">Competitors the filing names</span>
          <span className="biz-peers-v">{names.join(" · ")}</span>
          {/*
            Whose competitors these are is the correctness of the field: a registration
            statement can name the parent's competitors too, and quoting those would be
            verbatim, verifiable, and attached to the wrong company.
          */}
          <span className="biz-peers-note">named by the filing as this company's own</span>
        </div>
      )}
    </section>
  );
}

/**
 * The parent's and SpinCo's business descriptions, quoted from the filings, for the
 * situation page. Renders nothing when no business or peers fact has been read.
 *
 * @param facts The situation's facts; only the business and peers kinds are shown.
 */
export function Businesses({
  situation,
  facts,
}: {
  situation: SituationDetail;
  facts: FactResponse[];
}) {
  const byKind = new Map(facts.map((f) => [f.kind, f]));
  const parent = byKind.get("parent_business");
  const spinco = byKind.get("spinco_business");
  const peers = byKind.get("spinco_peers");

  // Nothing read yet describes either side, and no peers either: render nothing rather
  // than two empty boxes.
  if (!parent && !spinco && !peers) return null;

  return (
    <>
      <div className="sechd">The businesses</div>
      <div className="bizrow">
        <Block
          side={{
            label: "Parent",
            name: situation.parent.name,
            cik: situation.parent.cik,
            ticker: situation.parent.ticker,
            fact: parent,
          }}
        />
        <Block
          side={{
            label: "SpinCo",
            name: situation.spinco.name,
            cik: situation.spinco.cik,
            ticker: situation.spinco.ticker,
            fact: spinco,
          }}
          peers={peers}
        />
      </div>
    </>
  );
}
